import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { flowRoutes, interleave } from "../../lib/reviewFlowRoutes.js";
import { ACTION_CHIP, FILE_LABEL } from "./triageParts.jsx";
import {
  CAPTION_H, STEP_INK, NodeLabel, useInks, alpha, useCanvas, useReveal, clientRect,
  sampleCubic, sampleAcross, yAt, tracePath, hash,
} from "./ReviewFlowParts.jsx";

// The Findings flow drawn as the Convergence: a hairline per finding (or per
// `unit` of them, said in the corner), spread down the left edge. What the
// pipeline filtered out peels away and falls to a floor of dust; what it kept
// draws into one bundle, sorts into a lane for where Triage left it, and what
// was cleared settles at the end. What needs a person turns red and runs to a
// pin for the standard that flagged it.
//
// It is pointed at and opened as the Sankey is: every node -- a bar, a pin, a
// label, or the lines running into it -- shows the same card, keeps the lines
// through it and fades the rest; a click opens the tab that lists it.

const REVEAL_MS = 1700;
const SPARK_MS = 3200;
const LABEL_H = 16;
const PIN_LANE = 170;
const INKS = { sand: STEP_INK.written, kept: STEP_INK.kept, triaged: STEP_INK.triaged, met: STEP_INK.met, muted: "var(--muted)", ink: "var(--ink)", line: "var(--line-strong)" };
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const isFiltered = (r) => r.kind === "drop" && r.stage === "kept";

function layoutConvergence(model, W, H) {
  const { routes, unit, liftUnit } = flowRoutes(model);
  const g = { routes, unit, liftUnit, lines: [], lanes: [], pins: [], hits: [], labels: [] };
  if (!routes.length || !(W > 0)) return g;
  const lifts = routes.filter((r) => r.kind === "lift");
  const xPin = lifts.length ? W - PIN_LANE : W;
  const x0 = 2;
  const end = lifts.length ? xPin - 60 : W - 130;
  const x1 = x0 + (end - x0) * 0.4;
  const x2 = x0 + (end - x0) * 0.72;
  const yT = 3;
  const yB = H - 7;
  const Hf = yB - yT;
  const cy = yT + Hf * 0.42;
  Object.assign(g, { x0, x1, x2, x3: end, xPin, yT, yB, cy });

  const rank = (r) => (r.kind === "lift" ? 0 : r.kind === "end" ? 1 : r.id === "not_asked" ? 2 : 3);
  const carried = routes.filter((r) => !isFiltered(r)).sort((a, b) => rank(a) - rank(b));
  const nK = carried.reduce((n, r) => n + r.marks, 0);
  const bandK = Hf * 0.24;
  // Lanes at Triage, one per place a kept finding stopped.
  const band2 = Hf * 0.5;
  const gap = 5;
  const usable = band2 - gap * Math.max(0, carried.length - 1);
  let yy = cy - band2 / 2;
  const laneOf = new Map();
  for (const r of carried) {
    const h = Math.max(2, (usable * r.marks) / Math.max(1, nK));
    const lane = { route: r, y0: yy, y1: yy + h };
    laneOf.set(r.id, lane);
    g.lanes.push(lane);
    yy += h + gap;
  }
  const cleared = carried.find((r) => r.kind === "end") || null;
  const band3 = Hf * 0.2;
  const c3y = cy + Hf * 0.04;

  // A pin per standard that flagged something, spread around the bundle's
  // height in the Sankey's order.
  const span = Math.min(Hf - 8, lifts.length * 30);
  lifts.forEach((r, k) => g.pins.push({ route: r, y: cy - span / 2 + (span * (k + 0.5)) / lifts.length }));
  const pinOf = new Map(g.pins.map((p) => [p.route.id, p]));

  const sequence = interleave(routes);
  const n = sequence.length;
  const seen = new Map();
  let keptIndex = 0;
  for (let i = 0; i < n; i++) {
    const { route: r } = sequence[i];
    const j = seen.get(r.id) || 0;
    seen.set(r.id, j + 1);
    const y0 = yT + (Hf * (i + 0.5)) / n;
    const line = { route: r, i, jit: hash(i, 1) };
    const pts = [];
    if (isFiltered(r)) {
      const yTarget = cy + (y0 - cy) * 0.42;
      const xp = x0 + (x1 - x0) * (0.2 + hash(i, 2) * 0.74);
      const tmp = [];
      sampleAcross(tmp, x0, y0, x1, yTarget, 28, false);
      let px = tmp[0];
      let py = tmp[1];
      for (let k = 0; k < tmp.length; k += 2) {
        if (tmp[k] > xp) { const t = (xp - px) / ((tmp[k] - px) || 1); py += (tmp[k + 1] - py) * t; px = xp; pts.push(px, py); break; }
        px = tmp[k];
        py = tmp[k + 1];
        pts.push(px, py);
      }
      line.aEnd = pts.length / 2 - 1;
      const d1 = 18 + hash(i, 3) * 24;
      const c2x = px + d1 + 14 + hash(i, 4) * 18;
      sampleCubic(pts, px, py, px + d1, py, c2x, py + (yB - py) * 0.25, c2x + 10 + hash(i, 5) * 40, yB, 14, true);
    } else {
      const k = keptIndex++;
      const ky = cy - bandK / 2 + (bandK * (k + 0.5)) / Math.max(1, nK);
      const lane = laneOf.get(r.id);
      const ly = lane.y0 + ((lane.y1 - lane.y0) * (j + 0.5)) / r.marks;
      sampleAcross(pts, x0, y0, x1, ky, 24, false);
      line.aEnd = pts.length / 2 - 1;
      sampleAcross(pts, x1, ky, x2, ly, 20, true, 0.55);
      line.bEnd = pts.length / 2 - 1;
      if (r.kind === "lift") sampleAcross(pts, x2, ly, xPin, pinOf.get(r.id).y, 28, true, 0.58);
      else if (r === cleared) sampleAcross(pts, x2, ly, end, c3y - band3 / 2 + (band3 * (j + 0.5)) / r.marks, 16, true);
      else sampleAcross(pts, x2, ly, end, ly + 4, 16, true);
    }
    line.end = pts.length / 2 - 1;
    line.pts = Float32Array.from(pts);
    if (r.kind === "lift") {
      const cum = [0];
      for (let k = 1; k <= line.end; k++) cum.push(cum[k - 1] + Math.hypot(line.pts[k * 2] - line.pts[k * 2 - 2], line.pts[k * 2 + 1] - line.pts[k * 2 - 1]));
      line.cum = cum;
    }
    g.lines.push(line);
  }
  Object.assign(g, { bandK, band2, band3, c3y, cleared, nK });

  // Labels and what the pointer can land on, node by node.
  const hit = (id, x, top, w, h) => g.hits.push({ id, x, top, w, h });
  const label = (id, x, top, maxWidth) => g.labels.push({ id, x, top, maxWidth });
  if (model.nodes.get("written")?.count) { label("written", x0 + 6, yT, 90); hit("written", x0 - 4, yT, 10, Hf); }
  if (model.nodes.get("filtered")?.count) {
    label("filtered", x0 + (x1 - x0) * 0.55, yB - LABEL_H - 4, 150);
    hit("filtered", x0 + (x1 - x0) * 0.2, yB - 10, (x1 - x0) * 0.8 + 70, 14);
  }
  label("kept", x1 + 6, cy - bandK / 2 - LABEL_H - 3, 90);
  hit("kept", x1 - 5, cy - bandK / 2 - 3, 10, bandK + 6);
  if (model.nodes.get("triaged")?.count) {
    label("triaged", x2 + 6, cy - band2 / 2 - LABEL_H - 3, 90);
    hit("triaged", x2 - 5, cy - band2 / 2, 10, band2);
  }
  for (const lane of g.lanes) {
    if (lane.route.kind === "lift" || lane.route === cleared || lane.route.id === "kept") continue;
    label(lane.route.node, end + 6, (lane.y0 + lane.y1) / 2 - LABEL_H / 2 + 4, 150);
    hit(lane.route.node, end - 5, lane.y0, 10, lane.y1 - lane.y0 + 8);
  }
  if (cleared) {
    label(cleared.node, lifts.length ? end - 64 : end + 6, c3y + band3 / 2 + 3, 140);
    hit(cleared.node, end - 5, c3y - band3 / 2, 10, band3);
  }
  if (lifts.length) {
    label("met", xPin - 3, Math.max(0, g.pins[0].y - 34), 50);
    for (const p of g.pins) {
      label(p.route.node, xPin + 10, p.y - LABEL_H / 2, PIN_LANE - 16);
      hit(p.route.node, xPin - 7, p.y - 7, 14, 14);
    }
  }
  // Labels in the same column are kept apart.
  g.labels.sort((a, b) => a.x - b.x || a.top - b.top);
  for (let k = 1; k < g.labels.length; k++) {
    const a = g.labels[k - 1];
    const b = g.labels[k];
    if (Math.abs(a.x - b.x) < 40 && b.top < a.top + LABEL_H + 1) b.top = a.top + LABEL_H + 1;
  }
  return g;
}

// The line nearest the pointer at its x, within a few pixels; red lines win ties.
function lineAt(g, x, y) {
  let best = null;
  let bd = 5;
  for (const l of g.lines) {
    const yy = yAt(l.pts, x, l.end);
    if (yy == null) continue;
    const d = Math.abs(yy - y) - (l.route.kind === "lift" ? 4 : 0);
    if (d < bd) { bd = d; best = l; }
  }
  return best;
}

// The node a line is running into where the pointer is on it.
function nodeOnLine(g, line, x) {
  const r = line.route;
  if (isFiltered(r)) return "filtered";
  if (x <= g.x1 || r.id === "kept") return "kept";
  if (r.id === "waiting") return "waiting";
  if (x <= g.x2) return "triaged";
  return r.node;
}

export default function ReviewFlowConvergence({
  model, width, height, point = null, onPoint, onLeave, onOpen, caption, poolNote = "", onOpenLite = null,
}) {
  const boxRef = useRef(null);
  const g = useMemo(() => layoutConvergence(model, width, height), [model, width, height]);
  const inks = useInks(boxRef, INKS);
  const [canvasRef, getCtx] = useCanvas(width, height);
  const [started, setStarted] = useState(false);
  const t0 = useRef(0);
  const pointed = point?.node || null;

  const paint = useCallback((p, now = performance.now()) => {
    const ctx = getCtx();
    if (!ctx || !inks || !g.lines.length) return;
    if (p > 0 && !started) setStarted(true);
    const { x0, x1, x2, x3, xPin, yT, yB, cy, bandK, band3, c3y } = g;
    const F = p >= 1 ? Infinity : lerp(x0 - 20, xPin + 90, easeInOut(p));
    const front = (l) => (p >= 1 ? Infinity : F - l.jit * 70);
    const dark = document.documentElement.dataset.theme === "dark";
    // As the Sankey: what passes through the pointed node stays, the rest fades.
    const through = (r) => !pointed || r.path.includes(pointed);
    const em = (r) => (!pointed ? 1 : through(r) ? 1.3 : 0.16);

    // Gate guides.
    ctx.save();
    ctx.setLineDash([2, 5]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = alpha(inks.muted, 0.28);
    ctx.beginPath();
    for (const x of [x0, x1, x2, x3]) { ctx.moveTo(x + 0.5, yT); ctx.lineTo(x + 0.5, yB + 4); }
    ctx.stroke();
    ctx.restore();

    if (dark) ctx.globalCompositeOperation = "lighter";
    const thin = 0.75;
    // Filtered: neutral, then let go to the floor.
    const fm = !pointed ? 1 : pointed === "filtered" || pointed === "written" ? 1.3 : 0.16;
    ctx.lineWidth = thin;
    ctx.strokeStyle = alpha(inks.muted, 0.2 * fm);
    ctx.beginPath();
    for (const l of g.lines) if (isFiltered(l.route)) tracePath(ctx, l.pts, 0, l.aEnd, front(l));
    ctx.stroke();
    const fg = ctx.createLinearGradient(0, yT, 0, yB);
    fg.addColorStop(0, alpha(inks.muted, (dark ? 0.26 : 0.22) * fm));
    fg.addColorStop(1, alpha(inks.muted, 0.03 * fm));
    ctx.strokeStyle = fg;
    ctx.beginPath();
    for (const l of g.lines) if (isFiltered(l.route)) tracePath(ctx, l.pts, l.aEnd, l.end, front(l));
    ctx.stroke();
    // The dust floor where filtered findings land.
    ctx.fillStyle = alpha(inks.muted, (dark ? 0.5 : 0.45) * Math.min(1, fm));
    for (const l of g.lines) {
      if (!isFiltered(l.route)) continue;
      const ex = l.pts[l.end * 2];
      if (front(l) < ex) continue;
      const s = 1.1 + hash(l.i, 6) * 1.2;
      ctx.fillRect(ex - s / 2, yB + 2 - hash(l.i, 7) * 6 - s, s, s);
    }
    // Kept: warming toward the bundle, then into lanes and on. Hairlines stay
    // hairlines however many there are: the denser the bundle, the fainter each.
    const thinOut = clamp(1.6 / Math.max(0.1, g.nK / Math.max(1, bandK)), 0.22, 1);
    const ag = ctx.createLinearGradient(x0, 0, x1, 0);
    ag.addColorStop(0, alpha(inks.sand, (dark ? 0.3 : 0.35) * Math.max(0.5, thinOut)));
    ag.addColorStop(1, alpha(inks.kept, (dark ? 0.5 : 0.55) * thinOut));
    for (const l of g.lines) {
      if (isFiltered(l.route)) continue;
      ctx.globalAlpha = Math.min(1, em(l.route));
      ctx.strokeStyle = ag;
      ctx.lineWidth = thin;
      ctx.beginPath(); tracePath(ctx, l.pts, 0, l.bEnd, front(l)); ctx.stroke();
      if (l.route.kind !== "lift") {
        const settled = l.route === g.cleared;
        ctx.strokeStyle = alpha(settled ? inks.triaged : inks.muted, (settled ? 0.5 : 0.3) * thinOut);
        ctx.beginPath(); tracePath(ctx, l.pts, l.bEnd, l.end, front(l)); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";

    // What needs a person: red from the lanes on, glowing.
    const sinceDone = p >= 1 ? now - t0.current : 0;
    for (const l of g.lines) {
      if (l.route.kind !== "lift") continue;
      const m = Math.min(1, em(l.route));
      if (dark) { ctx.shadowBlur = 12; ctx.shadowColor = alpha(inks.met, 0.8 * m); }
      else { ctx.strokeStyle = alpha(inks.met, 0.12 * m); ctx.lineWidth = 5; ctx.beginPath(); tracePath(ctx, l.pts, l.bEnd, l.end, front(l)); ctx.stroke(); }
      ctx.strokeStyle = alpha(inks.met, m);
      ctx.lineWidth = pointed && through(l.route) ? 2 : 1.6;
      ctx.beginPath(); tracePath(ctx, l.pts, l.aEnd, l.end, front(l)); ctx.stroke();
      ctx.shadowBlur = 0;
      // Sparks run the red lines for a while after the draw-in.
      if (p >= 1 && sinceDone < SPARK_MS && l.cum) {
        const total = l.cum[l.cum.length - 1];
        const fade = 1 - sinceDone / SPARK_MS;
        const d0 = (((sinceDone / 1000) * 0.32 + hash(l.i, 8)) % 1) * total;
        for (let k = 0; k < 8; k++) {
          const d = d0 - k * 5;
          if (d < 0) break;
          let lo = 0;
          let hi = l.cum.length - 1;
          while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (l.cum[mid] <= d) lo = mid; else hi = mid; }
          const r = (d - l.cum[lo]) / ((l.cum[hi] - l.cum[lo]) || 1);
          const x = l.pts[lo * 2] + (l.pts[hi * 2] - l.pts[lo * 2]) * r;
          const y = l.pts[lo * 2 + 1] + (l.pts[hi * 2 + 1] - l.pts[lo * 2 + 1]) * r;
          ctx.fillStyle = alpha(inks.met, fade * (1 - k / 8) * m);
          const s = k === 0 ? 2.6 : 1.8;
          ctx.fillRect(x - s / 2, y - s / 2, s, s);
        }
      }
    }

    // Gate bars, each node's, faded with the node when another is pointed at.
    const gateOn = (x) => (p >= 1 ? 1 : clamp((F - x) / 120, 0, 1));
    const lit = (id) => !pointed || id === pointed || g.routes.some((r) => r.path.includes(pointed) && r.path.includes(id));
    ctx.fillStyle = alpha(inks.sand, 0.9 * (lit("written") ? 1 : 0.35));
    if (model.nodes.get("written")?.count) ctx.fillRect(x0 - 1, yT, 2.5, (yB - yT) * easeOut(gateOn(x0 - 10)));
    ctx.fillStyle = alpha(inks.kept, lit("kept") ? 1 : 0.35);
    ctx.fillRect(x1 - 1.5, cy - bandK / 2 - 3, 3, (bandK + 6) * easeOut(gateOn(x1)));
    for (const lane of g.lanes) {
      const r = lane.route;
      const a = (r.kind === "drop" ? 0.5 : 1) * (lit(r.node) ? 1 : 0.35);
      ctx.fillStyle = alpha(r.kind === "lift" ? inks.met : r === g.cleared ? inks.triaged : inks.muted, a);
      ctx.fillRect(x2 - 1.5, lane.y0 - 1, 3, (lane.y1 - lane.y0 + 2) * easeOut(gateOn(x2)));
    }
    if (g.cleared) { ctx.fillStyle = alpha(inks.triaged, 0.9 * (lit(g.cleared.node) ? 1 : 0.35)); ctx.fillRect(x3 - 1, c3y - band3 / 2 - 2, 2.5, (band3 + 4) * easeOut(gateOn(x3))); }
    // Pins, one per standard that flagged something.
    const pinOn = gateOn(xPin - 20);
    for (const pin of g.pins) {
      if (pinOn <= 0) continue;
      const on = lit(pin.route.node);
      const s = 7 * pinOn;
      if (dark) { ctx.shadowBlur = 14; ctx.shadowColor = alpha(inks.met, on ? 0.9 : 0.2); }
      ctx.fillStyle = alpha(inks.met, on ? 1 : 0.35);
      ctx.fillRect(xPin - s / 2, pin.y - s / 2, s, s);
      ctx.shadowBlur = 0;
      if (p >= 1 && sinceDone < SPARK_MS && on) {
        const ph = ((sinceDone / 1000) * 0.55 + pin.y * 0.01) % 1;
        const r = 4 + ph * 12;
        ctx.strokeStyle = alpha(inks.met, (1 - ph) * 0.5 * (1 - sinceDone / SPARK_MS));
        ctx.lineWidth = 1;
        ctx.strokeRect(xPin - r, pin.y - r, r * 2, r * 2);
      }
    }
  }, [g, inks, getCtx, started, pointed, model]);

  const signature = g.routes.length ? `${g.routes.map((r) => `${r.id}:${r.count}`).join(",")}` : "";
  const { done } = useReveal(inks ? signature : "", REVEAL_MS, (p) => { if (p >= 1) t0.current = performance.now(); paint(p); });
  // After the draw-in the sparks run their course; then the canvas repaints
  // only when the pointer or the theme changes something.
  useLayoutEffect(() => {
    if (!done.current) return undefined;
    let raf = 0;
    const tick = () => {
      paint(1);
      if (performance.now() - t0.current < SPARK_MS && !document.hidden) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [paint, done]);

  // What the pointer is on: a node's bar or pin, else the node the line under
  // it is running into.
  const nodeAt = (x, y) => {
    const h = g.hits.find((b) => x >= b.x && x <= b.x + b.w && y >= b.top && y <= b.top + b.h);
    if (h) return h;
    const line = lineAt(g, x, y);
    return line ? { id: nodeOnLine(g, line, x), x: x - 6, top: y - 6, w: 12, h: 12 } : null;
  };
  const at = (e) => { const r = e.currentTarget.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const onMove = (e) => {
    const [x, y] = at(e);
    const h = nodeAt(x, y);
    e.currentTarget.style.cursor = h && model.nodes.has(h.id) ? "pointer" : "default";
    if (!h || !model.nodes.has(h.id)) { if (pointed) onLeave(); return; }
    if (h.id !== pointed) onPoint(h.id, clientRect(e.currentTarget, h.x, h.top, h.w, h.h));
  };
  const onClick = (e) => {
    const [x, y] = at(e);
    const h = nodeAt(x, y);
    if (h && model.nodes.has(h.id)) onOpen(h.id);
  };

  const nodeOf = (id) => model.nodes.get(id);
  const dim = (id) => Boolean(pointed) && id !== pointed && !g.routes.some((r) => r.path.includes(pointed) && r.path.includes(id));
  const delay = (x) => Math.round((Math.max(0, x) / Math.max(1, width)) * REVEAL_MS * 0.9);
  const point$ = (id) => (e) => onPoint(id, e.currentTarget.getBoundingClientRect());
  const triageEmpty = !model.nodes.has("triaged") && !model.nodes.has("waiting");
  const standardEmpty = !triageEmpty && !model.nodes.has("triaged");
  const hidden = !started;
  const captions = width > 0 && g.lines.length ? [
    model.nodes.get("written")?.count ? ["written", g.x0] : null,
    ["kept", g.x1],
    model.nodes.has("triaged") ? ["triage", g.x2] : null,
    g.pins.length ? ["standard", g.xPin - 3] : null,
  ].filter(Boolean) : [];

  return (
    <div ref={boxRef} data-flow-view="convergence" className="absolute inset-0">
      {captions.map(([id, x]) => (
        <span key={id} data-flow-caption={id} className={`${FILE_LABEL} absolute top-0 whitespace-nowrap`} style={{ left: x }}>{caption(id)}</span>
      ))}
      {g.unit > 1 ? (
        <span data-flow-unit="" className="absolute right-0 text-[9px] leading-[1.4] tracking-[0.02em] text-[color:var(--muted-soft)]" style={{ top: CAPTION_H + height - 12 }}>
          each line is {g.unit.toLocaleString()} findings{g.liftUnit === 1 ? "; what needs you, one each" : `; what needs you, ${g.liftUnit.toLocaleString()} each`}
        </span>
      ) : null}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute left-0"
        style={{ top: CAPTION_H, width, height }}
        onPointerMove={onMove}
        onPointerLeave={() => pointed && onLeave()}
        onClick={onClick}
      />
      {width > 0 ? g.labels.map((l) => {
        const node = nodeOf(l.id);
        if (!node) return null;
        return (
          <NodeLabel key={l.id} node={node} lay={{ labelTop: l.top }} left={l.x} maxWidth={l.maxWidth} dim={dim(l.id)} hidden={hidden} delay={delay(l.x)}
            onPoint={point$(l.id)} onLeave={onLeave} onOpen={() => onOpen(l.id)} />
        );
      }) : null}
      {width > 0 && triageEmpty && poolNote ? (
        <span data-flow-note="pool" className={`${NOTE_CLASS} absolute`} style={{ left: g.x2, maxWidth: Math.max(120, width - g.x2), top: CAPTION_H + height / 2 - 12 }}>{poolNote}</span>
      ) : null}
      {width > 0 && standardEmpty ? (
        <span data-flow-note="untriaged" className="absolute grid justify-items-start gap-[6px]" style={{ left: g.x3, maxWidth: Math.max(120, width - g.x3), top: CAPTION_H + height / 2 - 22 }}>
          <span className={NOTE_CLASS}>Triage has not checked any of these yet.</span>
          {onOpenLite ? <button type="button" data-flow-open="lite" onClick={onOpenLite} className={ACTION_CHIP}>Open Triage</button> : null}
        </span>
      ) : null}
    </div>
  );
}
